import { COLOUR_METRIC_INFO, colourMetricDescription, colourMetricLabel } from './colourMetrics.js';
import { scoreWord } from './scoreWords.js';

// The map legend (both views; design A, 2026-10-23): a white card with the metric's name, an info icon
// (the Färgkarta menu's explanation) and a fold button, then the 11 colours as one vertical bar, best at the
// top. Betyg puts the whole scores 1–6 with a star beside it, each with its score word (scoreWords.js) beside
// its two colour rows: 6 and 5,5 are Exklusivt, 5 and 4,5 Eftertraktat, … 1 Ansträngt. A single measure labels
// the bar's ends and middle against the neighbourhood. Folded, only the name and the button show. On desktop
// it sits at the map's bottom right in both views (Map.js LegendDrawer, listViewPage.css).

const WHOLE_STARS = [6, 5, 4, 3, 2, 1];

const LEGEND_STEPS = [6, 5.5, 5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1];

// A single measure: the bar's ends and middle (top, middle, bottom). A high score is a higher value, except
// for unemployment, which is scored the other way round.
const LABELS = {
    higher: { sv: ['Mycket högre', 'Som närområdet', 'Mycket lägre'], en: ['Much higher', 'Like nearby areas', 'Much lower'] },
    lower: { sv: ['Mycket lägre', 'Som närområdet', 'Mycket högre'], en: ['Much lower', 'Like nearby areas', 'Much higher'] },
};

const STAR = '<svg class="legend-star" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3 6.1 20.6l1.3-6.6L2.5 9.4l6.6-.8z"></path></svg>';
const CHEVRON = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 4.5 L6 7.5 L9 4.5"></path></svg>';

const escapeHtml = (value) => String(value).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

export default function helpBoxData(checkedState, languageState, scoreToColor) {
    const swedish = Boolean(languageState?.swedish);
    const lang = swedish ? 'sv' : 'en';
    const metric = Object.keys(COLOUR_METRIC_INFO).find((key) => checkedState[key]) || null;
    const title = metric ? colourMetricLabel(metric, swedish) : (swedish ? 'Färger dolda' : 'Colours hidden');
    const labels = metric === 'totalScore' || !metric
        ? null
        : (metric === 'unemployment' ? LABELS.lower[lang] : LABELS.higher[lang]);

    const segments = LEGEND_STEPS.map((step, index) => (
        `<i class="legend-seg" style="grid-row:${index + 1};background-color:${scoreToColor[step]}"></i>`
    )).join('');
    const ticks = metric === 'totalScore'
        ? WHOLE_STARS.map((score) => (
            `<span class="legend-tick" style="grid-row:${LEGEND_STEPS.indexOf(score) + 1}">${score}${STAR}</span>`
        )).join('')
        : '';
    // Betyg: each whole star's word, covering the star's colour row and the half step under it.
    const bands = metric === 'totalScore'
        ? WHOLE_STARS.map((score) => (
            `<span class="legend-label is-band" style="grid-row:${LEGEND_STEPS.indexOf(score) + 1} / span ${score === 1 ? 1 : 2}">${escapeHtml(scoreWord(score, swedish))}</span>`
        )).join('')
        : '';
    const ends = labels
        ? `<span class="legend-label" style="grid-row:1">${labels[0]}</span>
           <span class="legend-label is-middle" style="grid-row:4 / 9">${labels[1]}</span>
           <span class="legend-label" style="grid-row:11">${labels[2]}</span>`
        : bands;

    const info = metric
        ? colourMetricDescription(metric, swedish)
        : (swedish ? 'Välj vad kartan färgas efter i Färgkarta.' : 'Pick what the map is coloured by in Colour map.');

    return `<div class="helpBoxContainer${ticks ? ' has-ticks' : ''}">
        <div class="helpBoxHeader">
            <span class="infoTitle">${escapeHtml(title)}</span>
            <span class="infoIcon" data-tooltip="${escapeHtml(info)}">i</span>
            <button class="helpBoxToggle" type="button" aria-label="${swedish ? 'Fäll ihop eller ut förklaringen' : 'Fold or unfold the legend'}">${CHEVRON}</button>
        </div>
        <div class="helpBoxBody">${segments}${ticks}${ends}</div>
    </div>`;
}
